import {
  QTypography,
  QTableSortableColumnHeader,
  QSearchBar,
  QTableHead,
  QTooltip,
  QAsyncTablePagination,
  QTableRow,
  QTableCell,
  QTableBody,
  QTable,
  QContainer,
} from '@kw/quokka-ui';
import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { RuleEntity } from '@kw/service-definitions-rule';
import type { ListResponse } from '@kw/rules-service/dist/src/types/ListResponse';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQPagination } from '../../kw-ui-components/Pagination/useQPagination';
import { useRuleSortParams } from './useRuleSortParams';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService } from '../../utils/fetchService';
import { getComparator } from '../../utils/getComparator';
import { PageLoader } from '../../PageLoader';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';

type RuleWithShortDescription<T> = Partial<T> & { descriptionShort: string };

export function Rules() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [searchQuery, setSearchQuery] = useState('');
  const flags: LaunchDarklyFlags['flags'] = useFlags();

  const { page, pageSize, setPage, handleChangePage, handleChangeRowsPerPage } = useQPagination(10);

  const path = `/eval/rules/list`;

  const { data: rulesData, error: rulesError } = useFetchService<ListResponse<RuleEntity>>(path, accessToken);
  const isLoading = !rulesData && !rulesError;

  const filteredRules = useMemo(() => {
    const rules = (rulesData?.results || []).map((rule: RuleWithShortDescription<RuleEntity>) => {
      const newRule = rule;
      if (rule.description.length > 75) {
        newRule.descriptionShort = `${rule.description.slice(0, 75)}...`;
      } else {
        newRule.descriptionShort = rule.description;
      }
      return newRule;
    });

    return searchQuery.length
      ? rules.filter(
          (rule: RuleWithShortDescription<RuleEntity>) =>
            rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            rule.description?.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : rules;
  }, [rulesData, searchQuery]);

  const total = filteredRules.length;
  const totalPageCount = Math.ceil(total / pageSize);

  const {
    ruleNameOrder,
    toggleRuleNameOrder,
    createdDateOrder,
    toggleCreatedDateOrder,
    updatedDateOrder,
    toggleUpdatedDateOrder,
    orderBy,
    setOrderBy,
  } = useRuleSortParams();

  const tableHeaders = [
    {
      title: 'Rule Name',
      onClick: toggleRuleNameOrder,
      sortValue: ruleNameOrder,
    },
    {
      title: 'Creation Date',
      onClick: toggleCreatedDateOrder,
      sortValue: createdDateOrder,
    },
    { title: 'Created By', onClick: () => {}, sortValue: null },
    {
      title: 'Last Updated',
      onClick: toggleUpdatedDateOrder,
      sortValue: updatedDateOrder,
    },
  ];

  // column header sorting UI filters
  const order = tableHeaders.map(({ sortValue }) => sortValue).find(o => o);

  useEffect(() => {
    if (ruleNameOrder) {
      setOrderBy('name');
    }

    if (createdDateOrder) {
      setOrderBy('createdAt');
    }

    if (updatedDateOrder) {
      setOrderBy('updatedAt');
    }

    if (!order) {
      setOrderBy(null);
    }
  }, [ruleNameOrder, createdDateOrder, updatedDateOrder, order, orderBy, setOrderBy]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, setPage]);

  if (rulesError) {
    console.error(rulesError);
    return <div>failed to load</div>;
  }

  return (
    <>
      {isLoading && <PageLoader />}
      <QContainer maxWidth="false">
        <BackgroundProgressBar />
        <QTypography>
          Rules help organizations define what specific risks and types of risks are acceptable. You can also create Policies by combining
          them with actions.
        </QTypography>
        <StyledTableWrapper>
          <QSearchBar
            id="search-rules"
            searchValue={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                setSearchQuery(e.target.value);
                setPage(0);
              }
            }}
            onRemove={() => {
              setSearchQuery('');
              setPage(0);
            }}
            placeholder="Search rules"
          />

          {total > 10 && (
            <QAsyncTablePagination
              page={page + 1}
              pagination={{
                dataPerPage: pageSize,
                totalData: total,
                lastPage: totalPageCount,
              }}
              getPage={newPage => setPage(newPage - 1)}
              showRowsPerPage={true}
              currentRows={(page + 1) * pageSize < filteredRules.length ? pageSize : filteredRules.length % pageSize}
              handlePageSizeChange={handleChangeRowsPerPage}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
            />
          )}

          <QTable aria-label="Rules-Table">
            <QTableHead>
              <QTableRow>
                {tableHeaders.map(({ title, onClick, sortValue }) => (
                  <QTableSortableColumnHeader
                    key={title}
                    title={title}
                    onClick={() => {
                      onClick();
                      setPage(0);
                    }}
                    sortValue={sortValue}
                  />
                ))}
              </QTableRow>
            </QTableHead>
            <QTableBody>
              {[...filteredRules]
                .sort(getComparator(order, orderBy))
                .slice(page * pageSize, (page + 1) * pageSize)
                .map((rule: RuleWithShortDescription<RuleEntity>) => (
                  <QTableRow key={rule.id}>
                    <QTableCell>
                      <QTypography>{rule.name}</QTypography>
                      <QTooltip title={rule.description} arrow>
                        <QTypography variant="smallRegular">{rule.descriptionShort}</QTypography>
                      </QTooltip>
                    </QTableCell>
                    <QTableCell>{dayjs(rule.createdAt).format('MMM DD, YYYY')}</QTableCell>
                    <QTableCell>Quokka Labs</QTableCell>
                    <QTableCell>{dayjs().diff(dayjs(rule.updatedAt), 'days')} day(s) ago by Quokka Labs</QTableCell>
                  </QTableRow>
                ))}
            </QTableBody>
          </QTable>
          {total > 10 && (
            <QAsyncTablePagination
              page={page + 1}
              pagination={{
                dataPerPage: pageSize,
                totalData: total,
                lastPage: totalPageCount,
              }}
              getPage={newPage => setPage(newPage - 1)}
              showRowsPerPage={true}
              currentRows={(page + 1) * pageSize < filteredRules.length ? pageSize : filteredRules.length % pageSize}
              handlePageSizeChange={handleChangeRowsPerPage}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
            />
          )}
        </StyledTableWrapper>
      </QContainer>
    </>
  );
}

/** styles */
const StyledTableWrapper = styled('div')`
  width: 100%;
  margin: 26px 0 0 0;
`;
